import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import SectionHeader from '../components/SectionHeader';
import { fmtCost } from '../utils/format';

const BANDS = [
  { key: 'under10', label: '< 10%',  accent: 'red',  test: (gp) => gp < 10 },
  { key: '10to20',  label: '10–20%', accent: 'gold', test: (gp) => gp >= 10 && gp < 20 },
  { key: '20to30',  label: '20–30%', accent: 'blue', test: (gp) => gp >= 20 && gp < 30 },
];

const BandCard = ({ label, count, accent, active, onClick }) => (
  <button
    onClick={onClick}
    className={`stat-card stat-card--${accent}`}
    style={{
      cursor: 'pointer',
      textAlign: 'left',
      border: active ? '2px solid var(--white)' : undefined,
      opacity: active === false ? 0.55 : 1,
    }}
  >
    <div className="stat-card__value">{count}</div>
    <div className="stat-card__label">{label}</div>
  </button>
);

const ProductMarginWatchDetail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const supplier = searchParams.get('supplier') || '';

  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [bandFilter, setBandFilter] = useState(null);

  const load = useCallback(async (supplierName) => {
    if (!supplierName) { setItems([]); return; }
    setLoading(true);
    const { data, error } = await supabase.rpc('get_product_margin_watch_items', { p_supplier: supplierName });
    if (error) {
      console.error('get_product_margin_watch_items error:', error);
      setItems([]);
    } else {
      setItems(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(supplier); }, [supplier, load]);

  const counts = useMemo(() => {
    const c = {};
    for (const b of BANDS) c[b.key] = items.filter(i => b.test(Number(i.gp_pct))).length;
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    if (!bandFilter) return items;
    const band = BANDS.find(b => b.key === bandFilter);
    return items.filter(i => band.test(Number(i.gp_pct)));
  }, [items, bandFilter]);

  return (
    <main className="app-main">
      <div className="page-nav">
        <button className="back-btn" onClick={() => navigate('/product-margin-watch')}>← Product Margin Watch</button>
        <span className="page-nav__title">{supplier || 'Product Margin'}</span>
        <button className="app-header__btn" onClick={() => load(supplier)}>↻ Refresh</button>
      </div>

      <div className="stats-row">
        {BANDS.map(b => (
          <BandCard
            key={b.key}
            label={b.label}
            count={counts[b.key]}
            accent={b.accent}
            active={bandFilter === b.key || bandFilter === null}
            onClick={() => setBandFilter(prev => prev === b.key ? null : b.key)}
          />
        ))}
      </div>

      <section className="section">
        <SectionHeader
          title={`Products Below Target Margin — ${supplier}`}
          count={filtered.length}
          onRefresh={() => load(supplier)}
          loading={loading}
        />
        <p className="section__desc">
          Gross profit calculated as (retail − cost×1.15) / retail, GST-adjusted on cost.
          Click a card above to filter by band, click again to clear.
        </p>

        {loading && <div className="loading-row">Loading…</div>}
        {!loading && !filtered.length && (
          <div className="empty-row">✓ No products below target margin for this supplier</div>
        )}

        {!loading && filtered.length > 0 && (
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Category</th>
                <th>Brand</th>
                <th style={{ textAlign: 'right' }}>Cost (excl GST)</th>
                <th style={{ textAlign: 'right' }}>Cost (incl GST)</th>
                <th style={{ textAlign: 'right' }}>Retail</th>
                <th style={{ textAlign: 'right' }}>GP %</th>
                <th style={{ textAlign: 'right' }}>GP $</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row, i) => (
                <tr key={`${row.code}-${i}`}>
                  <td>{row.code}</td>
                  <td>{row.name}</td>
                  <td>{row.category}</td>
                  <td>{row.brand}</td>
                  <td style={{ textAlign: 'right' }}>{fmtCost(row.cost_nzd)}</td>
                  <td style={{ textAlign: 'right' }}>{fmtCost(row.cost_incl_gst)}</td>
                  <td style={{ textAlign: 'right' }}>{fmtCost(row.retail_nzd)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: row.gp_pct < 10 ? 'var(--red-light, #f87171)' : 'var(--gold, #facc15)' }}>
                    {row.gp_pct}%
                  </td>
                  <td style={{ textAlign: 'right' }}>{fmtCost(row.gp_dollars)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </main>
  );
};

export default ProductMarginWatchDetail;
